import React, { useCallback, useEffect, useRef, useState } from "react";
import { compressImage, PhotoPayload } from "./imageUtil";
import "./PhotoUploader.css";

/**
 * PhotoUploader — the customer-facing photo experience used by every form.
 * Tap/click or drag-and-drop → instant thumbnails (numbered, removable, tap
 * to enlarge in a lightbox with prev/next). Photos are compressed in the
 * browser (~100–300 KB each) so uploads and emails stay fast.
 */

export type UploadedPhoto = { id: string; payload: PhotoPayload; preview: string; bytes: number };

type Props = {
  photos: UploadedPhoto[];
  onChange: (photos: UploadedPhoto[]) => void;
  max?: number;
  label?: string;
  hint?: string;
};

const fmtKB = (b: number) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const PhotoUploader: React.FC<Props> = ({ photos, onChange, max = 5, label = "Add photos of your items", hint }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [open, setOpen] = useState<number | null>(null);
  const [error, setError] = useState("");

  const addFiles = useCallback(async (list: FileList | File[] | null) => {
    if (!list) return;
    const files = Array.from(list).filter((f) => f.type.startsWith("image/"));
    if (!files.length) { setError("Please choose image files (JPG, PNG, HEIC)."); return; }
    const room = max - photos.length;
    if (room <= 0) { setError(`You can add up to ${max} photos.`); return; }
    setError("");
    setBusy(true);
    try {
      const results = await Promise.all(files.slice(0, room).map((f) => compressImage(f).catch(() => null)));
      const added: UploadedPhoto[] = results
        .filter((r): r is NonNullable<typeof r> => !!r)
        .map((r) => ({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          payload: r.payload,
          preview: r.preview,
          bytes: Math.round((r.payload.data.length * 3) / 4),
        }));
      onChange([...photos, ...added]);
      if (files.length > room) setError(`Only the first ${room} were added — max ${max} photos.`);
    } finally {
      setBusy(false);
    }
  }, [photos, onChange, max]);

  const remove = (i: number) => {
    const next = photos.filter((_, j) => j !== i);
    onChange(next);
    if (open !== null) setOpen(next.length ? Math.min(open, next.length - 1) : null);
  };

  // lightbox keyboard: Esc closes, arrows move
  useEffect(() => {
    if (open === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(null);
      if (e.key === "ArrowRight") setOpen((o) => (o === null ? o : (o + 1) % photos.length));
      if (e.key === "ArrowLeft") setOpen((o) => (o === null ? o : (o - 1 + photos.length) % photos.length));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, photos.length]);

  const full = photos.length >= max;

  return (
    <div className="pu">
      <div className="pu-head">
        <label className="pu-label">{label}</label>
        <span className="pu-count">{photos.length} of {max}</span>
      </div>
      {hint && <p className="pu-hint">{hint}</p>}

      <div className="pu-grid">
        {photos.map((p, i) => (
          <div className="pu-thumb" key={p.id}>
            <button type="button" className="pu-thumb-btn" onClick={() => setOpen(i)} aria-label={`Open item ${i + 1}`}>
              <img src={p.preview} alt={`Your item ${i + 1}`} />
            </button>
            <span className="pu-num">{i + 1}</span>
            <span className="pu-size">{fmtKB(p.bytes)}</span>
            <button type="button" className="pu-remove" onClick={() => remove(i)} aria-label={`Remove item ${i + 1}`}>✕</button>
          </div>
        ))}

        {!full && (
          <button
            type="button"
            className={`pu-add${drag ? " drag" : ""}${busy ? " busy" : ""}`}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
            disabled={busy}
          >
            <span className="pu-add-icon" aria-hidden="true">{busy ? "…" : "+"}</span>
            <span className="pu-add-text">{busy ? "Preparing…" : photos.length ? "Add another photo" : "Add photos"}</span>
            <span className="pu-add-sub">{photos.length ? "" : "Tap to choose · or drag & drop"}</span>
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
      />

      {error && <p className="pu-error" role="alert">{error}</p>}
      {photos.length > 0 && !error && (
        <p className="pu-ok">✓ These {photos.length === 1 ? "is the photo" : `are the ${photos.length} photos`} we’ll send to Everything Eco.</p>
      )}

      {open !== null && photos[open] && (
        <div className="pu-lb" role="dialog" aria-label="Photo preview" onClick={() => setOpen(null)}>
          <div className="pu-lb-inner" onClick={(e) => e.stopPropagation()}>
            <img src={photos[open].preview} alt={`Your item ${open + 1}`} />
            <div className="pu-lb-bar">
              <button type="button" onClick={() => setOpen((open - 1 + photos.length) % photos.length)} disabled={photos.length < 2} aria-label="Previous">‹</button>
              <span>{open + 1} / {photos.length}</span>
              <button type="button" onClick={() => setOpen((open + 1) % photos.length)} disabled={photos.length < 2} aria-label="Next">›</button>
              <button type="button" className="pu-lb-remove" onClick={() => remove(open)}>Remove</button>
              <button type="button" className="pu-lb-close" onClick={() => setOpen(null)}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoUploader;
